import { formatDate } from 'lib/helpers/dateUtils';
import { isPrimitiveValue } from 'lib/helpers/utility';

import {
  CATEGORY_TABLE_COLUMNS_STYLE,
  CELL_LENGTH_MULTIPLE,
  GAP,
  MIN_COLUMN_WIDTH,
  PORTRAIT_MAX_COLUMNS,
  TABLE_FONT_SIZE
} from './constants';

const NO_LINEBREAK_COLUMNS = ['id', 'date'];

export const filterColumns = (columns = []) => {
  return columns.filter((col) => col.field && col.field !== 'actions' && !col.hide);
};

const getValueLength = (value) => {
  return isPrimitiveValue(value) ? String(value || '').length : 1;
};

const getColumnMaxWidth = (header, rows, columnIndex) => {
  const maxLength = Math.max(
    getValueLength(header),
    ...rows.map((row) => {
      const value = row[columnIndex];
      return getValueLength(value);
    })
  );

  return maxLength * CELL_LENGTH_MULTIPLE; // Adjust multiplier based on font size (~5px per char)
};

export const getColumnStyles = (columnHeaders, rows, category, rowsSlice = 0) => {
  const maxColWidth = Math.max(...columnHeaders.map((col) => getValueLength(col)));

  const columnStyles = columnHeaders.reduce((acc, header, i) => {
    const splittedHeader = header.split(' ');

    const customStyle = CATEGORY_TABLE_COLUMNS_STYLE?.[category]?.[header] || {};

    acc[i] = { ...customStyle };

    // set minCellWidth for NO_LINEBREAK_COLUMNS
    if (splittedHeader.some((part) => NO_LINEBREAK_COLUMNS.includes(part.toLowerCase()))) {
      const currValueIdx = rowsSlice + i; // get index of header value from rows
      const rowValue = rows?.[0]?.[currValueIdx];

      // if value has primitive type then apply the style, this fixes the issue when a date col has an array with dates
      if (isPrimitiveValue(rowValue)) {
        const columnWidth = getColumnMaxWidth(header, rows, currValueIdx);

        const computedMinWidth = Math.max(columnWidth, maxColWidth, MIN_COLUMN_WIDTH);
        acc[i] = {
          ...acc[i],
          minCellWidth: Math.max(acc[i].minCellWidth ?? 0, computedMinWidth)
        };
      }
    }

    // apply global minimum width so columns are never too narrow
    const currentMin = acc[i].minCellWidth ?? acc[i].cellWidth ?? 0;
    acc[i].minCellWidth = Math.max(currentMin, MIN_COLUMN_WIDTH);

    return acc;
  }, {});

  return columnStyles;
};

//generate the page number and date
export const generatePageNumberAndDate = (doc, timezone = null) => {
  const totalPages = doc.internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    doc.setFontSize(TABLE_FONT_SIZE);
    doc.setTextColor(0, 0, 0);

    const printDate = formatDate(new Date(), timezone, 'MM/dd/yyyy h:mm:ss a');
    const pageNumber = `${i} of ${totalPages}`;

    const dateX = pageWidth - 60;
    const centerX = pageWidth / 2;
    const y = pageHeight - GAP;

    // Estimate width/height of white background rectangles
    const rectHeight = 6; // adjust based on font size
    const rectWidthDate = 60;
    const rectWidthPage = 40;

    // Draw white rectangles behind text
    doc.setFillColor(255, 255, 255);
    doc.rect(dateX - 2, y - 4.5, rectWidthDate, rectHeight, 'F'); // for print date
    doc.rect(centerX - rectWidthPage / 2 - 2, y - 4.5, rectWidthPage, rectHeight, 'F'); // for page number

    // Draw text
    doc.text(`Print date: ${printDate}`, dateX, y);
    doc.text(pageNumber, centerX, y, { align: 'center' });
  }
};

// reformat cells, especially last row with totals
export const formatCells = (data, foot) => {
  if (foot && data.section === 'foot') {
    // Only apply top border
    data.cell.styles.lineWidth = { top: 0.5, right: 0, bottom: 0, left: 0 };
    data.cell.styles.lineColor = {
      top: [0, 0, 0],
      right: [255, 255, 255],
      bottom: [255, 255, 255],
      left: [255, 255, 255]
    };

    // Don't fill the background
    delete data.cell.styles.fillColor;

    // text color
    data.cell.styles.textColor = [0, 0, 0];
  }
};

export const didDrawCell = (data) => {
  const { section, cell, doc } = data;

  if (section === 'body' && typeof cell.raw !== 'string' && !!cell.raw?.strike) {
    const startX = cell.x;
    const endX = startX + doc.getTextWidth(cell.text.join(' ')) + 3;
    const y = cell.y + cell.height / 2;

    doc.setLineWidth(0.2);
    doc.setDrawColor(0);
    doc.line(startX, y, endX, y);
  }
};

export const splitSmartChunks = (array, maxChunkSize = PORTRAIT_MAX_COLUMNS) => {
  const length = array.length;

  const numChunks = Math.ceil(length / maxChunkSize);
  const chunkSize = Math.ceil(length / numChunks);

  const result = [];
  for (let i = 0; i < length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }

  return result;
};

const calculateRowsPerPage = (doc, startY, rowHeight = GAP) => {
  const pageHeight = doc.internal.pageSize.height;

  const usableHeight = pageHeight - startY;

  return Math.floor(usableHeight / rowHeight);
};

const estimateRowHeight = {
  portrait: ({ doc, rows }) => {
    const pageWidth = doc.internal.pageSize.width;

    const heights = rows.flatMap((row) => {
      const rowWidth = row.reduce(
        (sum, cellText) => sum + (String(cellText ?? '')?.length || 0),
        0
      );

      const lines = Math.floor(rowWidth / pageWidth) + 1;

      return lines * TABLE_FONT_SIZE;
    });

    const uniqueHeights = [...new Set(heights)];
    const avg = uniqueHeights.reduce((sum, h) => sum + h, 0) / uniqueHeights.length; // avg of heights
    return Math.floor(avg);
  },
  landscape: ({ doc, rows, colWidth }) => {
    const lineHeight = TABLE_FONT_SIZE;

    const heights = rows.map((row) => {
      let maxLines = 1;

      row.forEach((cellText) => {
        const text = String(cellText ?? '');
        const wrappedLines = doc.splitTextToSize(text, colWidth);
        maxLines = Math.max(maxLines, wrappedLines.length);
      });

      return maxLines * lineHeight;
    });

    // Find the most frequent height (mode)
    const counts = heights.reduce((acc, curr) => ((acc[curr] = (acc[curr] || 0) + 1), acc), {});
    const mostFrequentHeight = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0]; // key with highest count
    return Math.floor((Number(mostFrequentHeight) + lineHeight) / 2);
  }
};

export const splitRowsChunk = ({ doc, startY, rows, orientation, columnChunks }) => {
  const tableWidth = doc.internal.pageSize.width;
  const colWidth = tableWidth / columnChunks?.[0]?.length || 1;

  const rowHeight = estimateRowHeight[orientation]?.({ doc, rows, colWidth });

  const firstPageRows = calculateRowsPerPage(doc, startY, rowHeight);
  const rowsPerPage = calculateRowsPerPage(doc, 0, rowHeight);

  const chunkedRows = [];

  chunkedRows.push(rows.slice(0, firstPageRows));

  for (let i = firstPageRows; i < rows.length; i += rowsPerPage) {
    chunkedRows.push(rows.slice(i, i + rowsPerPage));
  }
  return chunkedRows;
};

// Draw a simple horizontal separator
export const drawHorizontalLine = (doc, y, options = {}) => {
  const pageWidth = doc.internal.pageSize.width;
  const { startX = 0, endX = pageWidth, color = '#E9EAEB', lineWidth = 0.2 } = options;

  doc.setDrawColor(color);
  doc.setLineWidth(lineWidth);
  doc.line(startX, y, endX, y);
};
